import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { useYowanAuth } from '../app/YowanAuthContext';
import { fetchYowanAdminStagingDomainFallbackState } from '../app/yowanContentClient';
import {
  AdminBlogShell,
  AdminPageHeader,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
const { yowanPageContent } = require('./pageContent');

const targetLabels = {
  touzen: '刀禅',
  yowan: '揺腕法',
};
const STAGING_SWITCH_DEVELOPER_EMAIL = 'shimazaki@shimashou.com';

const canUseStagingSwitch = (currentUser) =>
  String(currentUser?.email || '').toLowerCase() === STAGING_SWITCH_DEVELOPER_EMAIL;

const getTargetLabel = (target) => targetLabels[target] || '未設定';

const getEnabledLabel = (enabled) => {
  if (enabled === true) {
    return '有効';
  }

  if (enabled === false) {
    return '無効';
  }

  return '不明';
};

const getErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data || {};

  return responseData.detail || responseData.message || fallbackMessage;
};

const StateField = ({ label, value, tone = '' }) => (
  <div className="yowan-staging-switch__state-field">
    <p className="yowan-staging-switch__state-label">{label}</p>
    <p className={`yowan-staging-switch__state-value${tone ? ` yowan-staging-switch__state-value--${tone}` : ''}`}>
      {value || '-'}
    </p>
  </div>
);

const AdminStagingDomainFallbackPage = () => {
  const content = yowanPageContent['/admin/deploy/staging-domain-fallback'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const { currentUser } = useYowanAuth();
  const canShowStagingSwitch = canUseStagingSwitch(currentUser);
  const [state, setState] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState('');

  const loadState = ({ resetMessage = true } = {}) => {
    setIsLoading(true);
    if (resetMessage) {
      setMessage('');
    }

    return fetchYowanAdminStagingDomainFallbackState()
      .then((payload) => {
        setState(payload);
        return payload;
      })
      .catch((error) => {
        setState(null);
        setMessage(getErrorMessage(error, content?.messages?.loadError || 'Staging表示状態を読み込めませんでした。'));
        return null;
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    if (!canShowStagingSwitch) {
      setIsLoading(false);
      return;
    }

    loadState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canShowStagingSwitch]);

  const allowedTargets = useMemo(() => {
    const source = state?.allowedTargets || ['touzen', 'yowan'];
    return source.filter((target) => target === 'touzen' || target === 'yowan');
  }, [state]);

  if (!content) {
    return null;
  }

  const currentTarget = state?.config?.target || '';
  const currentEnabled = state?.config?.enabled;
  const currentTargetTone = currentTarget === 'yowan' ? 'yowan' : currentTarget === 'touzen' ? 'touzen' : 'unknown';
  const backToAdmin = appendYowanPreviewQueryIfNeeded('/admin', preservedDomainSearch);

  if (!canShowStagingSwitch) {
    return (
      <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
        <AdminPageHeader
          title={content.header.title}
          paragraphs={['このページは開発者専用です。']}
          actions={[
            <Link key="back-to-admin" to={backToAdmin}>
              Dashboardへ戻る
            </Link>,
          ]}
        />
      </AdminBlogShell>
    );
  }

  const reloadCurrentPage = () => {
    window.location.reload();
  };

  const openHerokuappRoot = () => {
    window.location.assign('/');
  };

  return (
    <AdminBlogShell activeNavHref={content.activeNavHref} routeKey={content.routeKey}>
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
        actions={[
          <Link key="back-to-admin" to={backToAdmin}>
            Dashboardへ戻る
          </Link>,
        ]}
      />

      {isLoading ? <LoadingSpinner /> : null}

      <section className="yowan-admin-detail-panel yowan-staging-switch">
        <div className="yowan-staging-switch__header">
          <h2 className="yowan-admin-detail-panel__title">{content.statePanel.title}</h2>
          <p className="yowan-admin-detail-panel__lead">{content.statePanel.lead}</p>
        </div>

        <div className="yowan-staging-switch__state-grid">
          <StateField label={content.labels.appName} value={state?.appName} />
          <StateField label={content.labels.environment} value={state?.environment} />
          <StateField label={content.labels.enabled} value={getEnabledLabel(currentEnabled)} />
          <StateField label={content.labels.target} value={getTargetLabel(currentTarget)} tone={currentTargetTone} />
          <StateField label={content.labels.allowedTargets} value={allowedTargets.map(getTargetLabel).join(' / ')} />
        </div>

        <div className="yowan-staging-switch__actions" aria-label="切替反映確認">
          <button
            type="button"
            className="c-form__submit yowan-staging-switch__target-button"
            onClick={reloadCurrentPage}
          >
            現在のページを再読み込み
          </button>
          <button
            type="button"
            className="c-form__submit yowan-staging-switch__target-button"
            onClick={openHerokuappRoot}
          >
            Herokuappトップを開き直す
          </button>
        </div>

        {message ? <p className="yowan-admin-form__message">{message}</p> : null}
      </section>

      <section className="yowan-admin-detail-panel yowan-staging-switch__notes">
        <h2 className="yowan-admin-detail-panel__title">{content.notes.title}</h2>
        <ul className="yowan-admin-detail-panel__list">
          {content.notes.items.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>
    </AdminBlogShell>
  );
};

export default AdminStagingDomainFallbackPage;
